import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import hero from "@/assets/gazebo-privacy-panels.jpg";
import hotTubPanel from "@/assets/hot-tub-privacy-panel-commercial-95-black.jpg";
import pergolaTop from "@/assets/pergola-top-navy-cover-poolside.png.asset.json";

import heroPicture from "@/assets/gazebo-privacy-panels.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/gazebo-privacy-panels")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Gazebo Covers & Privacy Panels | Hill Country Series™ — Texas Made Shade",
      description:
        "Custom gazebo and pergola tops plus privacy panels in Commercial 95 HDPE and Phifertex Plus mesh. Hill Country Series™ shade, built-to-fit in Seguin, TX.",
      image: hero,
      imageAlt: "Custom dark-bronze gazebo privacy panels installed on a black aluminum pergola — HDPE shade mesh by Texas Made Shade",
      socialTitle: "Gazebo Covers & Privacy Panels — Hill Country Series™",
      socialDescription:
        "Tops for pergolas and gazebos plus custom privacy panels in Commercial 95 and Phifertex Plus mesh. Made-to-measure in Seguin, Texas.",
      keywords:
        "gazebo covers, gazebo tops, pergola tops, pergola covers, gazebo privacy panels, gazebo privacy screens, outdoor privacy panels, pergola privacy screens, custom privacy panels Texas, Commercial 95 privacy screen, Phifertex Plus privacy panel, Hill Country Series, backyard privacy shade, Seguin TX gazebo covers",
    }),
    scripts: serviceHeadScripts({
      path: "/gazebo-privacy-panels",
      serviceName: "Gazebo Covers & Privacy Panels",
      breadcrumbName: "Gazebo Covers & Privacy Panels",
      description:
        "Custom-built gazebo and pergola tops and privacy panels in Commercial 95 HDPE shade fabric and Phifertex Plus vinyl mesh — Hill Country Series™, fabricated in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/gazebo-privacy-panels"
      serviceName="Gazebo Covers & Privacy Panels"
      breadcrumbs={[
        { label: "Shade & Outdoor Living", to: "/pergola-covers" },
        { label: "Gazebo Covers & Privacy Panels" },
      ]}
      eyebrow="★ HILL COUNTRY SERIES™ · GAZEBO COVERS & PRIVACY PANELS ★"
      title="Gazebo Covers & Privacy Panels — Tops and Screens Built-to-Fit in Seguin, TX"
      intro="We build both halves of the outdoor room: the top and the sides. Custom shade tops for pergolas and gazebos, plus privacy panels for the walls — all sewn from Commercial 95 HDPE shade mesh, Phifertex Plus vinyl mesh or Sunbrella® marine canvas. Block the western sun, cut the wind, and screen out the neighbors without giving up your breeze or your view. Part of our Hill Country Series™ outdoor living collection, patterned and finished in our Seguin, Texas canvas shop."
      heroImage={heroPicture}
      hideLetsTalkForm
      heroCta={{
        eyebrow: "Hill Country Series™ · Seguin, TX",
        headline: "Need a Top, Privacy Panels, or Both? Get a Free Quote.",
        subhead:
          "Send us your gazebo or pergola dimensions, whether you want a shade top, side panels or a full system, and we'll come back same business day with fabric options and a no-pressure written estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Gazebo Covers & Privacy Panels")}&notes=${encodeURIComponent("Gazebo cover / privacy panel inquiry — please describe your gazebo or pergola (size, frame material, color), whether you need a shade top, side panels or both, and whether you want fixed or removable panels.")}`,
        primaryLabel: "Get My Free Cover & Panel Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}

      features={[
        "Gazebo & pergola tops — rod-pocket, laced, wave or fixed-tension",
        "Custom-cut to your gazebo or pergola — down to the inch",
        "Commercial 95 HDPE shade mesh — blocks 90–95% UV, lets air through",
        "Phifertex Plus vinyl mesh — easy-clean, mildew-resistant, see-through privacy",
        "Sunbrella® marine canvas tops for solid, water-shedding shade",
        "Reinforced webbing borders with grommets every 6–12\"",
        "Stainless cable, snap-hook or bungee mounting hardware",
        "Fixed, removable or roll-up configurations",
        "Color-matched binding and thread (UV-stable PTFE / Tenara on request)",
        "Hill Country Series™ — made in Seguin, TX for San Antonio, Austin, Hill Country & Gulf Coast",
      ]}
      body={[
        {
          heading: "Tops for Pergolas & Gazebos — Not Just Side Panels",
          text: "The top is where the heat comes from. We fabricate custom shade tops that mount right to your existing pergola rafters or gazebo frame — grommeted and laced with stainless cable for a drum-tight fit, sewn into rod pockets, or built as a soft wave canopy. Mesh tops knock down 90–95% of the UV while breathing; Sunbrella® canvas tops give you solid shade that sheds rain.",
          bullets: [
            "Grommet-and-cable laced tops — taut, flat, no flapping",
            "Rod-pocket and wave canopies for a softer, draped look",
            "Sunbrella® canvas tops for solid shade and rain shedding",
            "Commercial 95 / Phifertex Plus mesh tops that breathe",
            "Removable for storms and winter — reinstall in minutes",
            "Retrofit onto wood, cedar or aluminum frames you already own",
          ],
          cta: { label: "See More Pergola & Gazebo Cover Options", href: "/pergola-covers" },
        },
        {

          heading: "Privacy + Shade Without Closing In Your Gazebo",
          text: "A solid wall blocks the view but also kills the breeze and turns your gazebo into an oven. Our mesh privacy panels do the opposite — 90–95% sun and sight blockage on the outside, full air movement on the inside. You stay cool, you stay private, and you keep the open feel of an outdoor room.",
          bullets: [
            "90–95% UV and visual screening from outside",
            "Air still moves through — no trapped heat",
            "Soft daylight inside, no harsh glare",
            "Doesn't sag or flap when properly tensioned",
          ],
        },
        {
          heading: "Two Fabrics, Two Looks — Commercial 95 & Phifertex Plus",
          text: "Commercial 95 is a knitted HDPE shade fabric — soft, matte, fabric-like, perfect for that draped resort look. Phifertex Plus is a vinyl-coated polyester mesh — tighter weave, sleeker finish, wipes clean with a hose. Both are rated for years of Texas sun and wind.",
          bullets: [
            "Commercial 95 — softer drape, warmer texture, 10+ year UV warranty",
            "Phifertex Plus — crisper look, easy-clean vinyl, mildew-resistant",
            "Available in browns, blacks, greys, sand, forest and more",
            "Pair either fabric with our pergola covers for a fully matched system",
          ],
          cta: { label: "See Gazebo-Pergola Covers & Fabric Colors", href: "/pergola-covers" },
        },
        {
          heading: "Mounting Options — Fixed, Removable or Roll-Up",
          text: "How you mount the panel matters as much as the fabric. We pattern each job to your existing gazebo, pergola or fence frame and build the hardware around how you actually use the space.",
          bullets: [
            "Grommet + bungee-cord — quick on/off for storms and seasons",
            "Stainless cable runs with snap hooks — clean, taut, marine-grade",
            "Welded aluminum frames with laced-in panels for a sail-tight finish",
            "Roll-up versions with sewn-in webbing straps and clips",
            "Side-curtain track systems for retractable privacy walls",
          ],
          cta: { label: "Welding & Custom Fabrication", href: "/custom-fabrication" },
        },
        {
          heading: "Built for Texas Weather — Sun, Wind & Storms",
          text: "Every panel is sewn with UV-stable thread, reinforced corner patches, and double-stitched webbing borders. We can build them strong enough to leave up year-round, or light enough to drop and roll in 60 seconds when a storm rolls in off the Gulf.",
          bullets: [
            "Double-stitched 1\" or 2\" webbing borders",
            "Reinforced corner patches and grommet washers",
            "PTFE / Tenara thread upgrade for maximum UV life",
            "Stainless or marine-grade hardware throughout",
          ],
        },
      ]}
      applicationsEyebrow="WHERE THEY GO"
      applicationsHeading="Where Custom Privacy Panels Work Best"
      applications={[
        "Backyard Gazebos",
        "Aluminum & Wood Pergolas",
        "Patio Side Walls",
        "Pool & Hot Tub Enclosures",
        "Outdoor Kitchens",
        "Apartment Balconies",
        "Restaurant Patios",
        "Fence-Top Privacy Extensions",
        "Carport Side Screens",
        "RV & Trailer Awning Walls",
      ]}
      cityCallouts={[]}
      faqs={[
        {
          q: "How private are mesh gazebo privacy panels really?",
          a: "From outside in bright daylight, you get 90–95% visual blockage with both Commercial 95 and Phifertex Plus — people walking by see a solid color, not into your space. From inside looking out you keep a soft, shaded view. At night with interior lights on, all mesh panels become more transparent, so for nighttime privacy we recommend pairing with solid drop curtains.",
        },
        {
          q: "Will the panels hold up to Texas wind and sun?",
          a: "Yes — both fabrics carry 10+ year UV warranties from the mill, and we sew with UV-stable thread, reinforced borders and marine-grade hardware. For sustained 40+ mph winds we recommend a removable or roll-up design so you can drop them before storms.",
        },
        {
          q: "Can you match the panels to my existing pergola or gazebo color?",
          a: "Yes — we stock the full Commercial 95 and Phifertex Plus color lines and can match binding, webbing and thread to your frame color. Black-on-black, bronze-on-bronze and sand-on-white are the most popular combinations.",
          aJsx: (
            <p>
              Yes — we stock the full <Link to="/pergola-covers" className="font-semibold text-navy-deep underline">Commercial 95 and Phifertex Plus color lines</Link> and can match binding, webbing and thread to your frame color. Black-on-black, bronze-on-bronze and sand-on-white are the most popular combinations.
            </p>
          ),
        },
        {
          q: "Do you install, or is it ship-only?",
          a: "We install locally throughout the San Antonio, Austin, Hill Country and Gulf Coast region. For customers outside our install footprint we ship finished panels with hardware kits and clear measuring instructions.",
        },
        {
          q: "How do I measure my gazebo for privacy panels?",
          a: "Measure each opening width × height between the posts you want screened, and tell us how high off the ground you want the panel to start and stop. We'll send a quick measuring sheet with your quote — or come measure on-site if you're in our service area.",
          aJsx: (
            <p>
              Measure each opening width × height between the posts you want screened, and tell us how high off the ground you want the panel to start and stop. We'll send a quick measuring sheet with your quote — or come measure on-site if you're in our{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">San Antonio / Hill Country / Gulf Coast service area</Link>.
            </p>
          ),
        },
      ]}
      postCta={(
        <>
        <section className="border-t-4 border-navy-deep bg-navy-deep py-16 text-white">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="grid gap-10 md:grid-cols-[auto_1fr] md:items-center">
              <div className="flex justify-center">
                <SeriesBadge slug="hill-country-series" size="md" />
              </div>
              <div>
                <div className="font-display text-sm tracking-[0.3em] text-red">★ HILL COUNTRY SERIES™ ★</div>
                <h2 className="mt-2 font-display text-3xl font-bold uppercase md:text-4xl">
                  Gazebo Covers & Privacy Panels Are Part of the Hill Country Series™
                </h2>
                <p className="mt-4 text-lg text-white/85">
                  The Hill Country Series™ is our luxury outdoor-living collection — motorized patio screens,
                  premium retractable shade, backyard upholstery, lighting, misting and heat. Custom gazebo and
                  pergola tops and privacy panels are the fabric backbone of that system: the top for the sun,
                  the panels for the wind and the neighbors.
                </p>
                <ul className="mt-6 space-y-2 text-white/90">
                  <li>• Matched fabrics, binding and hardware across the whole outdoor room</li>
                  <li>• Upgrade later to motorized screens, misting, heat or lighting</li>
                  <li>• Ranger Care™ maintenance available on every Hill Country Series™ install</li>
                </ul>
                <Link
                  to="/hill-country-series"
                  className="mt-6 inline-block border-2 border-red bg-red px-6 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition hover:bg-white hover:text-navy-deep"
                >
                  Explore the Hill Country Series™
                </Link>
              </div>
            </div>
          </div>
        </section>
        <section className="bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <div>
                <div className="font-display text-sm tracking-[0.3em] text-red">★ PERGOLA & GAZEBO TOPS ★</div>
                <h2 className="mt-2 font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
                  We Put Tops on Pergolas and Gazebos
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  A custom top turns an open frame into real shade. We pattern to your rafters, hem with
                  reinforced webbing, set stainless grommets and lace the cover down with marine cable so it
                  stays flat and quiet in Texas wind — in mesh that breathes or Sunbrella® canvas that sheds rain.
                </p>
                <ul className="mt-6 space-y-2 text-navy-deep">
                  <li>• Grommet-and-cable laced tops, drum tight</li>
                  <li>• Mesh or solid canvas, dozens of colors</li>
                  <li>• Removable for storms and winter storage</li>
                  <li>• Pairs with matching side privacy panels</li>
                </ul>
              </div>
              <figure className="overflow-hidden border-2 border-navy-deep/10 bg-white shadow-card">
                <img
                  src={pergolaTop.url}
                  alt="Custom navy shade top laced with stainless cable onto a cedar pergola over a poolside patio dining set at dusk — gazebo and pergola covers by Texas Made Shade"
                  width={1280}
                  height={1280}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <figcaption className="border-t-2 border-navy-deep bg-navy-deep px-5 py-3 font-display text-xs uppercase tracking-[0.3em] text-white">
                  Hill Country Series™ · Laced Pergola Top · Poolside Patio
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="bg-cream py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <figure className="overflow-hidden border-2 border-navy-deep/10 bg-white shadow-card">
                <img
                  src={hotTubPanel}
                  alt="Custom black Commercial 95 HDPE privacy panel by Texas Made Shade installed alongside a backyard hot tub, blocking the neighbor's view while keeping airflow"
                  width={1600}
                  height={1067}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <figcaption className="border-t-2 border-navy-deep bg-navy-deep px-5 py-3 font-display text-xs uppercase tracking-[0.3em] text-white">
                  Commercial 95 · Black · Hot Tub Privacy Panel
                </figcaption>
              </figure>
              <div>
                <div className="font-display text-sm tracking-[0.3em] text-red">★ HOT TUB PRIVACY</div>
                <h2 className="mt-2 font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
                  Screen the Hot Tub From the Neighbors — Without Losing the Breeze
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Commercial 95 HDPE shade mesh in <strong>black</strong> gives you a matte, opaque wall from the
                  outside while still breathing on the inside. Perfect for hot tubs, spas and pool decks where
                  you want privacy from adjacent yards and two-story windows — with no trapped humidity and no
                  boxed-in feel.
                </p>
                <ul className="mt-6 space-y-2 text-navy-deep">
                  <li>• 95% UV & sight blockage from outside</li>
                  <li>• Air moves through — steam and heat vent naturally</li>
                  <li>• Matte black finish disappears against modern hot tubs</li>
                  <li>• Tensioned between welded posts, cables or existing pergola</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        <section className="border-t-4 border-navy-deep bg-white py-20">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">★ RELATED SERVICES</div>
              <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">Pairs Well With</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                Round out your outdoor room with matching shade and canvas from the same Seguin shop.
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Link to="/pergola-covers" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Gazebo-Pergola Covers</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Rod-pocket, wave and fixed shade tops in Commercial 95 and Phifertex Plus.</p>
              </Link>
              <Link to="/sail-shades" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Sail Shades</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Custom triangle and quad shade sails for patios, pools and play areas.</p>
              </Link>
              <Link to="/curtains" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Outdoor Curtains</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Roll-down weather curtains and drop walls for porches, patios and gazebos.</p>
              </Link>
            </div>
          </div>
        </section>
        </>
      )}
    />
  ),
});
